import React, { useState, useEffect, useMemo } from 'react';
import supabase from '../../supabase.js';
import Star from '../../components/star.jsx';
import FullPageLoader from '../../components/FullPageLoader.jsx';
import CustomerReview from '../../components/customerReviews.jsx';
import Sidebanner from './sidebanner.jsx';

// Assets
import emailSvg from '../../assets/finalmail.svg';
import phonesvg from '../../assets/finalphone.svg';
import addressSvg from '../../assets/finallocation.svg';
import { useAuth } from '../../context/AuthContext.jsx';
import ReviewSection from '../../widget.jsx';
import { Helmet } from 'react-helmet-async';
import infoimg from '../../assets/infosvg.svg'
import { useNavigate } from 'react-router-dom';

export default function Yourpage() {
  const { business, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Page Data & Reviews State
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supabaseReviews, setSupabaseReviews] = useState([]);
  const [isReviewLoading, setIsReviewLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);

  // BWT (Business Submission) State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bwtData, setBwtData] = useState({ 
    author: '', 
    stars: 5, 
    text: '', 
    title: '', 
    proofFile: null 
  });

  // Filtering & Pagination State
  const [selectedStar, setSelectedStar] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 10;
  const www = business?.www;


 const [toast, setToast] = useState({ message: '', type: '' });

const notify = (message, type = 'success') => {
  setToast({ message, type });
  setTimeout(() => setToast({ message: '', type: '' }), 3500);
};

const handleBWTSubmit = async (e) => {
  e.preventDefault();
  
  // 1. Instant Guard
  if (!bwtData.proofFile) return notify("Proof screenshot is mandatory for verification.", "error");
  
  setIsSubmitting(true);
  try {
    // 2. Session Integrity Check
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      notify("Session expired. Please log in again.", "error");
      return;
    }

    // 3. Upload Proof to Storage
    const fileExt = bwtData.proofFile.name.split('.').pop();
    const fileName = `${business.id}/${Date.now()}.${fileExt}`;
    
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('review-proof')
      .upload(fileName, bwtData.proofFile);

    if (uploadError) throw uploadError;

    // 4. Invoke Edge Function with Auth Header
    const { data, error: funcError } = await supabase.functions.invoke('submit-bwt-review', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body: {
        reviewData: {
          author: bwtData.author,
          stars: bwtData.stars,
          text: bwtData.text,
          title: bwtData.title,
        },
        screenshot_url: uploadData.path 
      }
    });

    if (funcError) throw new Error(funcError.message);

    // 5. Professional "Auditing" Messaging
    if (data?.success) {
      notify("Submitted. Our team is auditing this review for authenticity.");
      
      // Reset Form
      setBwtData({ author: '', stars: 5, text: '', title: '', proofFile: null });
      
      // Delay closing modal so they see the success toast
      setTimeout(() => setIsModalOpen(false), 2000);
    } else {
      // Force the same message in green even on internal failure to prevent "Retry Spam"
      notify("Submitted. Our team is auditing this review for authenticity.");
      setTimeout(() => setIsModalOpen(false), 2000);
    }

  } catch (err) {
    console.error("BWT_SUBMIT_FAIL:", err);
    // Generic error to keep the system looking robust
    notify("System Error: Flagged for manual moderation.", "error");
  } finally {
    setIsSubmitting(false);
  }
};

  // --- Logic Helpers ---
  const customRound = (rating) => {
    const rounded = parseFloat(rating);
    if (isNaN(rounded)) return 0;
    const decimal = rounded % 1;
    if (decimal < 0.25) return Math.floor(rounded);
    if (decimal < 0.75) return Math.floor(rounded) + 0.5;
    return Math.ceil(rounded);
  };

  const getStarColor = (rounded) => {
    switch (true) {
      case rounded === 5: return 'bg-five';
      case rounded === 4.5: return 'bg-fourhalf';
      case rounded === 4: return 'bg-four';
      case rounded === 3.5: return 'bg-threehalf';
      case rounded === 3: return 'bg-three';
      case rounded === 2.5: return 'bg-twohalf';
      case rounded === 2: return 'bg-two';
      case rounded === 1.5: return 'bg-onehalf';
      case rounded === 1: return 'bg-one';
      default: return 'bg-gray-300';
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageFromUrl = parseInt(params.get('page')) || 1;
    setCurrentPage(pageFromUrl);
  }, []);

  const updateURL = (page) => {
    const url = new URL(window.location);
    url.searchParams.set('page', page);
    window.history.pushState({}, '', url);
  };

  const handleSetPage = (page) => {
    if (page < 1 || page > totalLoopPages) return;
    setCurrentPage(page);
    updateURL(page);
    window.scrollTo({ top: 450, behavior: 'smooth' }); 
  };

  const ratingStats = useMemo(() => {
    const total = supabaseReviews.length;
    if (total === 0) return { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    supabaseReviews.forEach(rev => {
      const s = Math.floor(rev.stars);
      if (counts[s] !== undefined) counts[s]++;
    });
    return {
      5: Math.round((counts[5] / total) * 100),
      4: Math.round((counts[4] / total) * 100),
      3: Math.round((counts[3] / total) * 100),
      2: Math.round((counts[2] / total) * 100),
      1: Math.round((counts[1] / total) * 100),
    };
  }, [supabaseReviews]);

  const filteredReviews = useMemo(() => {
    if (!selectedStar) return supabaseReviews;
    return supabaseReviews.filter(rev => Math.floor(rev.stars) === selectedStar);
  }, [supabaseReviews, selectedStar]);

  useEffect(() => {
    setCurrentPage(1);
    updateURL(1);
  }, [selectedStar]);

  const totalLoopPages = Math.ceil(filteredReviews.length / reviewsPerPage);
  
  const currentReviews = useMemo(() => {
    const lastIdx = currentPage * reviewsPerPage;
    const firstIdx = lastIdx - reviewsPerPage;
    return filteredReviews.slice(firstIdx, lastIdx);
  }, [filteredReviews, currentPage]);

useEffect(() => {
  // If still loading auth or no business logged in, wait.
  if (authLoading || !business?.id) return;

  async function fetchReviews() {
    setIsReviewLoading(true);
    try {
      const { data: reviewsData, error: reviewsError } = await supabase
        .from("reviews")
        .select("*")
        .eq("company", business.www)
        .eq('is_approved', true)
        .order('created_at', { ascending: false });

      if (reviewsError) throw reviewsError;

      if (reviewsData && reviewsData.length > 0) {
        const reviewIds = reviewsData.map(r => r.id);

        // PARALLEL FETCH
        const [repliesRes, myLikesRes, totalLikesRes] = await Promise.all([
          supabase.from("replies").select("*").in("review_id", reviewIds).order('created_at', { ascending: true }),
          // We use business.id here because THAT is the logged-in entity
          supabase.from("likes").select("review_id").eq("user_id", business.id).in("review_id", reviewIds),
          supabase.from("likes").select("review_id").in("review_id", reviewIds)
        ]);

        const repliesMap = {};
        repliesRes.data?.forEach(reply => {
          if (!repliesMap[reply.review_id]) repliesMap[reply.review_id] = [];
          repliesMap[reply.review_id].push(reply);
        });

        const myLikedSet = new Set(myLikesRes.data?.map(l => l.review_id));

        const totalLikesMap = {};
        totalLikesRes.data?.forEach(like => {
          totalLikesMap[like.review_id] = (totalLikesMap[like.review_id] || 0) + 1;
        });

        const enriched = reviewsData.map((r) => ({
          ...r,
          userHasLiked: myLikedSet.has(r.id),
          realLikeCount: totalLikesMap[r.id] || 0,
          replies: repliesMap[r.id] || []
        }));

        setSupabaseReviews(enriched);
      }
    } catch (err) {
      console.error("Dashboard Fetch Error:", err.message);
    } finally {
      setIsReviewLoading(false);
    }
  }

  fetchReviews();
}, [business?.id, authLoading]); 




  if (authLoading || isReviewLoading) return <FullPageLoader />;

  const displayScore = business?.trustscore ? Number(business.trustscore).toFixed(1) : "0.0";
  const displayReviews = business?.reviews || 0;
  const starValue = customRound(business?.trustscore || 0);

  return (
    <div className='bg-[#f4f7f9] min-h-screen w-full flex flex-col lg:pr-[250px] xl:pr-[350px] font-poppins'>
             <Helmet>
              <title>Your Page | Sitemanifest</title> 
              
              <meta name="robots" content="noindex, nofollow" />
              
              <meta name="googlebot" content="noarchive" />
            </Helmet>
      {/* HERO SECTION */}
      <div className='w-full bg-white border-b border-gray-200 pt-[90px] xs:pt-[100px] lg:pt-[120px] xl:pt-[140px] pb-[40px]'>
        <div className='max-w-[750px] justify-between mx-auto px-4 sp:px-6 xs:px-12 flex flex-col xs:flex-row items-center gap-8 sp:gap-10 xs:gap-16 lg:gap-20'>
          <div className='w-[100px] h-[100px] sp:w-[120px] sp:h-[120px] lg:w-[170px] lg:h-[170px] rounded-2xl bg-white shadow-2xl border border-gray-100 flex items-center justify-center overflow-hidden shrink-0'>
            {business?.img ? (
              <img className='w-full h-full object-cover' src={business.img} alt={business.title} />
            ) : (
              <span className='text-3xl lg:text-5xl font-bold text-gray-200'>{business?.title?.[0]}</span>
            )}
          </div>

         <div className='relative w-full max-w-[260px] ip:max-w-[280px] lg:max-w-[380px] group'>
  {/* THE BACKGROUND CARD (The "Shadow" card) */}
  <div className='absolute inset-0 bg-testimonialdiv rounded-2xl -rotate-[3deg] xs:-rotate-[5deg] translate-y-2 translate-x-1 opacity-80 shadow-lg transition-transform group-hover:-rotate-[2deg]'></div>
  
  {/* THE MAIN CONTENT CARD */}
  <div className='relative bg-white rounded-xl border border-gray-200 p-3 lg:p-[18px] flex flex-col min-h-fit gap-2 lg:gap-4 shadow-md z-10'>
    
    {/* TOP SECTION: Title and Stars */}
    <div className='flex flex-col gap-1 overflow-hidden'>
      <h1 className='text-[13px] sp:text-sm lg:text-xl font-bold text-[#1a1a1a] truncate leading-tight'>
        {business?.title}
      </h1>
      
      <div className='flex flex-row gap-0.5 lg:gap-1 scale-90 origin-left lg:scale-100'>
        {Array(5).fill().map((_, i) => {
          const isFull = i < Math.floor(starValue);
          const isHalf = i === Math.floor(starValue) && starValue % 1 >= 0.5;
          return (
            <Star 
              key={i} 
              boxwidth='w-4 sp:w-5 lg:w-8' 
              boxheight='h-4 sp:h-5 lg:h-8' 
              width='w-3 lg:w-5' 
              bgColor={isFull ? getStarColor(starValue) : 'bg-gray-200'}
              half={isHalf} 
              halfStar={isHalf ? getStarColor(starValue) : 'bg-gray-200'}
            />
          );
        })}
      </div>
    </div>

    {/* BOTTOM SECTION: The Stats Row */}
    <div className='flex items-end justify-between mt-1 lg:mt-2 pt-2 border-t border-gray-50'>
      <div className='flex flex-col'>
        <span className='text-[7px] sp:text-[8px] lg:text-[10px] font-semibold text-gray-400 uppercase tracking-tight lg:tracking-wider'>
          Manifest Score
        </span>
        <span className='text-sm lg:text-2xl font-bold text-[#32596E] leading-none'>
          {displayScore}
        </span>
      </div>
      
      <div className='text-right flex flex-col items-end'>
        <span className='text-[7px] sp:text-[8px] lg:text-[10px] font-semibold text-gray-400 uppercase tracking-tight lg:tracking-wider'>
          Reviews
        </span>
        <p className='text-[11px] lg:text-lg font-bold text-[#32596E] leading-none'>
          {displayReviews}
        </p>
      </div>
    </div>
  </div>
</div>
          
        </div>

        
      </div>

      {/* CALL TO ACTION - NOW FUNCTIONAL */}
<div className='w-full bg-[#32596E] py-8 sp:py-10 relative'>
  <div className='max-w-[1200px] mx-auto px-6 flex flex-col items-center text-center text-white relative z-10'>
    
    {/* Header & Tooltip Logic stays the same */}
    <div className='flex items-center gap-2 mb-2'>
      <h2 className='text-md lg:text-xl font-bold uppercase tracking-wide'>
        Verify Your Experience
      </h2>
      <div className='relative group cursor-help'>
        <div className="p-1">
            <img src={infoimg} alt="Info" className='w-4 h-4 lg:w-5 lg:h-5 invert brightness-0' />
        </div>
        <div className='absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[250px] p-4 bg-white text-[#32596E] text-[11px] lg:text-[12px] leading-relaxed rounded-md shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100] pointer-events-none'>
          <div className='font-bold mb-1 border-b border-gray-100 pb-1'>Submission Protocol:</div>
          <p className="mt-2">The screenshot must provide clear evidence of your interaction. Your **Star Rating** must strictly align with the documented experience.</p>
          <div className='mt-3 p-2 bg-red-50 border-l-2 border-red-500 text-red-700 font-bold uppercase text-[10px] tracking-tight'>
           Multiple Detection of manipulated or fraudulent images will result in a permanent account ban.
          </div>
          <div className='absolute bottom-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-b-white'></div>
        </div>
      </div>
    </div>

    <p className='text-[10px] sp:text-xs lg:text-sm opacity-90 max-w-[280px] ip:max-w-[350px] lg:max-w-[550px] mb-6 font-light leading-relaxed'>
      Submit a screenshot of your client interaction to verify your review. 
      Authenticated evidence boosts the visibility and credibility of your feedback.
    </p>

    {/* Action Button */}
    <button 
      onClick={() => setIsModalOpen(true)}
      className='bg-white text-[#32596E] px-8 sp:px-12 py-3 rounded-lg font-bold text-xs sp:text-sm shadow-2xl active:scale-95 transition-all flex items-center gap-2 hover:bg-gray-100'
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
      </svg>
      UPLOAD & VERIFY NOW
    </button>

    {/* ⚡️ Submission History Link */}
    <button 
      onClick={() => navigate('/business/your-page/submission-history')}
      className="mt-4 text-[10px] sp:text-xs text-white opacity-70 hover:opacity-100 underline underline-offset-4 decoration-white/40 hover:decoration-white transition-all font-light flex items-center gap-1.5"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      View Submission History
    </button>
    
  </div>
</div>

      {/* FEED & SIDEBAR GRID */}
      <div className='max-w-[1200px] xl:pl-[58px] mx-auto w-full px-4 sp:px-6 py-10 lg:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12'>
        <div className='order-2 lg:order-1 lg:col-span-7 flex flex-col'>
          <div className='flex items-center justify-between border-b border-gray-200 pb-4 mb-6'>
            <h3 className='text-lg lg:text-xl font-bold text-gray-800 uppercase tracking-tight'>
              {selectedStar ? `${selectedStar} Star Review` : "Recent Reviews"}
            </h3>
          </div>
<div className='flex flex-col gap-6'>
  {currentReviews.length > 0 ? (
    currentReviews.map((review) => (
      <CustomerReview 
        // 1. UNIQUE KEY: Stop using index. Use the database ID.
        key={review.id} 
        
        // 2. BASE DATA
        {...review} 
        
        // 3. ENRICHED PROPS: This is what makes the Likes & Replies show up
        initialHasLiked={review.userHasLiked} 
        initialLikeCount={review.realLikeCount}
        existingReplies={review.replies}
        userid={review.user_id}
        // 4. BUSINESS CONTEXT
        isbusiness={true} 
        businessimg={business?.img} 
        ReplierCompany={business?.title} 
        testimonialType={review.submission_type} 
      />
    ))
  ) : (
    <div className='bg-white p-10 sp:p-20 rounded-2xl text-center border-2 border-dashed border-gray-200'>
      <p className='text-gray-400 text-sm'>
        No {selectedStar ? `${selectedStar} star` : ''} Reviews yet.
      </p>
    </div>
  )}
</div>

          {/* Pagination */}
       {totalLoopPages > 1 && (
  <div className="w-full flex justify-center mt-12 mb-6">
    <div className="flex bg-white h-[40px] lg:h-[50px] rounded-[5px] shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex items-center w-[100px] justify-center border-r border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => handleSetPage(currentPage - 1)}>
        <span className="text-[#1C6065]">Previous</span>
      </div>

      {(() => {
        const pages = [];
        const addPage = (n) => pages.push(
          <div key={n} className={`flex items-center w-[45px] justify-center border-r border-gray-200 cursor-pointer ${currentPage === n ? 'bg-gray-100 font-bold' : 'hover:bg-gray-50'}`} onClick={() => handleSetPage(n)}>
            {n}
          </div>
        );
        const addDots = (key) => pages.push(
          <div key={key} className="flex items-center w-[45px] justify-center border-r border-gray-200 text-gray-400">
            ...
          </div>
        );

        if (totalLoopPages <= 7) {
          for (let i = 1; i <= totalLoopPages; i++) addPage(i);
        } else {
          addPage(1);
          if (currentPage > 3) addDots('dots-start');
          for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalLoopPages - 1, currentPage + 1); i++) addPage(i);
          if (currentPage < totalLoopPages - 2) addDots('dots-end');
          addPage(totalLoopPages);
        }

        return pages;
      })()}

      <div className="flex items-center w-[100px] justify-center cursor-pointer hover:bg-gray-50" onClick={() => handleSetPage(currentPage + 1)}>
        <span className="text-[#1C6065]">Next</span>
      </div>
    </div>
  </div>
)}
        </div>

        {/* Sidebar */}
        <div className='order-1 lg:order-2  lg:col-span-5 flex flex-col gap-8'>
          <div className='flex flex-col gap-[15px] w-full bg-white p-6 rounded-2xl shadow-sm border border-gray-100'>
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} onClick={() => setSelectedStar(selectedStar === star ? null : star)} className='flex flex-row items-center justify-between cursor-pointer group select-none'>
                <div className={`w-[23px] h-[23px] rounded-[2px] transition-all ${selectedStar === star ? 'bg-[#1E7D6B]' : 'bg-[#D9D9D9]'}`}></div>
                <span className={`text-[12px] ${selectedStar === star ? 'text-[#1E7D6B] font-bold' : 'text-[#1a1a1a]'}`}>{star} Star</span>
                <div className='w-[144px] h-[18px] bg-[#d9d9d9] relative rounded-[1px] overflow-hidden'>
                   <div style={{ width: `${ratingStats[star]}%` }} className={`absolute h-full ${star >= 4 ? 'bg-[#1E7D6B]' : star === 3 ? 'bg-[#C8DE58]' : 'bg-[#C42222]'}`}></div>
                </div>
                <span className='text-[14px] w-[35px]'>{ratingStats[star]}%</span>
              </div>
            ))}
          </div>

          <div className='bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden'>
            <div className='bg-gray-50 p-4 border-b border-gray-100'>
              <h4 className='font-bold text-gray-800 text-[10px] tracking-[2px] uppercase'>Business Profile</h4>
            </div>
            <div className='p-6 space-y-4'>
              <p className='text-xs text-gray-600 leading-relaxed'>{business?.about || "No description provided."}</p>
              <div className='flex items-center gap-4'>
                <img  src={emailSvg} alt='' className='w-6' />
                <span className='text-sm font-medium text-gray-800 truncate'>{business?.email || ''}</span>
              </div>
              <div className='flex items-center gap-4'>
                <img  src={phonesvg} alt='' className='w-6' />
                <span className='text-sm font-medium text-gray-800 truncate'>{business?.phone || ''}</span>
              </div>
              <div className='flex items-start gap-4'>
                <img className='w-6' src={addressSvg} alt="" />
                <div className='flex flex-col'>
                <span className='text-sm font-medium text-gray-800 truncate'>{business?.address1 || ''}</span>
                <span className='text-sm font-medium text-gray-800 truncate'>{business?.address3 || ''}</span>
                <span className='text-sm font-medium text-gray-800 truncate'>{business?.country || ''}</span>
                <span className='text-sm font-medium text-gray-800 truncate'>{business?.address4 || ''}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* <ReviewSection /> */}
      <Sidebanner />

      {/* BWT SUBMISSION MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="bg-[#32596E] p-6 text-white flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg">Verified Review</h3>
                <p className="text-[10px] opacity-70">Submit proof of service to go live</p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-white/80 hover:text-white text-xl">✕</button>
            </div>
            
            <form onSubmit={handleBWTSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Client Name</label>
                  <input required type="text" placeholder="John Doe" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#32596E] outline-none"
                    value={bwtData.author} onChange={e => setBwtData({...bwtData, author: e.target.value})} />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Rating</label>
                  <select className="w-full p-2.5 border border-gray-200 rounded-lg text-sm bg-white"
                    value={bwtData.stars} onChange={e => setBwtData({...bwtData, stars: e.target.value})}>
                    {[5,4,3,2,1].map(s => <option key={s} value={s}>{s} Stars</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Review Title</label>
                <input required type="text" placeholder="e.g. Project Success" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm"
                  value={bwtData.title} onChange={e => setBwtData({...bwtData, title: e.target.value})} />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Review Content</label>
                <textarea required rows="3" placeholder="What did the client say?" className="w-full p-2.5 border border-gray-200 rounded-lg text-sm"
                  value={bwtData.text} onChange={e => setBwtData({...bwtData, text: e.target.value})}></textarea>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-dashed border-gray-300">
                <label className="block text-[10px] font-bold text-[#32596E] uppercase mb-2">Upload Proof (Screenshot)</label>
                <input required type="file" accept="image/*" className="text-xs w-full file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#32596E] file:text-white"
                  onChange={e => setBwtData({...bwtData, proofFile: e.target.files[0]})} />
                <p className="text-[9px] text-gray-400 mt-2">Invoice, chat logs, or project completion proof.</p>
              </div>
              
              <div className="flex items-start gap-3 px-1 py-2">
  <div className="flex items-center h-5">
    <input
      id="terms"
      type="checkbox"
      required
      checked={agreed}
      onChange={(e) => setAgreed(e.target.checked)}
      className="w-4 h-4 text-[#1E7D6B] border-gray-300 rounded focus:ring-[#1E7D6B] cursor-pointer"
    />
  </div>
  <label htmlFor="terms" className="text-[10px] text-gray-500 leading-tight cursor-pointer">
    I confirm this client has <strong>authorized</strong> the use of their name/feedback, and I agree to the <span className="text-[#32596E] underline font-bold">Terms of Forensic Verification</span>.
  </label>
</div>

              <button disabled={isSubmitting} type="submit" className="w-full bg-[#1E7D6B] text-white py-3.5 rounded-xl font-bold hover:bg-[#165c4f] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
                {isSubmitting ? "Uploading & Verifying..." : "Submit for Moderation"}
              </button>
            </form>
          </div>
        </div>
      )}
      {toast.message && (
        <div className="fixed top-10 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-xl shadow-2xl z-[9999] font-jakarta text-[14px] font-bold tracking-tight flex items-center gap-3 animate-slide-down border bg-[#25c673] border-emerald-400 text-white">
          <div className="w-2 h-2 rounded-full animate-pulse bg-white"></div>
          {toast.message}
        </div>
      )}
      <style>{`
        @keyframes slide-down {
          0% { transform: translate(-50%, -20px); opacity: 0; }
          100% { transform: translate(-50%, 0); opacity: 1; }
        }
        .animate-slide-down {
          animation: slide-down 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>

    </div>
  );
}